import { useQueryClient } from '@tanstack/react-query'

interface ProfileWithDateFormat {
  preferences?: { date_format?: DateFormat }
}

export type DateFormat = 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD'
const DEFAULT_FORMAT: DateFormat = 'MM/DD/YYYY'

// ── Pure formatters (no React dependency) ──────────────────────────────────

// A PHI-masked date arrives already formatted by the server (e.g. "**/**/2026").
// It must NOT be reparsed — new Date() would fail, and the old year-only form was
// rendering as a fake 01/01. Pass any value containing '*' straight through.
function isMaskedDate(v: unknown): v is string {
  return typeof v === 'string' && v.includes('*')
}

/**
 * Resolve an ISO value to calendar Y/M/D parts.
 *
 * A bare date-only string ('YYYY-MM-DD', e.g. date_of_service) has NO timezone,
 * so we read its digits literally. `new Date('YYYY-MM-DD')` would anchor it at
 * UTC midnight, and reading it back with local getters then shows the previous
 * day for any viewer behind UTC — which is exactly why the same chart's DOS
 * rendered a day apart for users in different timezones. Values that carry a
 * time component (real timestamps) still go through `new Date` + local getters,
 * where rendering in the viewer's timezone is the intended behaviour.
 */
function dateParts(iso: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (m) return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) }
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() }
}

/**
 * Format an ISO date string as a date-only string using the given format.
 * Returns '--' for null/undefined/invalid input.
 */
export function formatDate(iso: string | null | undefined, fmt: DateFormat | string = DEFAULT_FORMAT): string {
  if (!iso) return '--'
  if (isMaskedDate(iso)) return iso
  const parts = dateParts(iso)
  if (!parts) return '--'

  const year = parts.year
  const month = String(parts.month).padStart(2, '0')
  const day = String(parts.day).padStart(2, '0')

  // Some call-sites pass a plain string from server/user profile; treat unknown formats as default.
  const safeFmt: DateFormat = fmt === 'DD/MM/YYYY' || fmt === 'YYYY-MM-DD' || fmt === 'MM/DD/YYYY'
    ? fmt
    : DEFAULT_FORMAT

  switch (safeFmt) {
    case 'DD/MM/YYYY': return `${day}/${month}/${year}`
    case 'YYYY-MM-DD': return `${year}-${month}-${day}`
    case 'MM/DD/YYYY':
    default: return `${month}/${day}/${year}`
  }
}

/**
 * Format an ISO date string as a date + time string using the given format.
 * Returns '--' for null/undefined/invalid input.
 */
export function formatDateTime(iso: string | null | undefined, fmt: DateFormat | string = DEFAULT_FORMAT): string {
  if (!iso) return '--'
  if (isMaskedDate(iso)) return iso
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '--'

  const datePart = formatDate(iso, fmt as DateFormat)
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${datePart} ${timePart}`
}

/**
 * Format an ISO date string as a short date (e.g., "Mar 15" for charts/axes).
 * This intentionally does NOT change with user preference — it's for compact chart labels.
 */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '--'
  if (isMaskedDate(iso)) return iso
  const parts = dateParts(iso)
  if (!parts) return '--'
  // Build a local Date from the resolved calendar parts so a date-only value is
  // never timezone-shifted (see dateParts).
  const d = new Date(parts.year, parts.month - 1, parts.day)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Format a date-only ISO value as "1 Oct 2026" — day, abbreviated month, year.
 * Used where a status must say WHEN it changes ("Active from 1 Oct 2026"), so the
 * month is spelled rather than numeric and the year is never dropped. Like
 * formatShortDate this does not follow the user's date preference: it is label
 * copy, not a data column.
 */
export function formatDayMonthYear(iso: string | null | undefined): string {
  if (!iso) return '--'
  if (isMaskedDate(iso)) return iso
  const parts = dateParts(iso)
  if (!parts) return '--'
  const d = new Date(parts.year, parts.month - 1, parts.day)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Format time-only from ISO (e.g., "2:30 PM").
 * Time format is not affected by date preference.
 */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '--'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '--'
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
}

// ── React hook — reads preference from profile query cache ─────────────────

/**
 * Returns the user's date_format preference from the profile query cache.
 * If profile isn't loaded yet, returns the default ('MM/DD/YYYY').
 *
 * Use this to get the format and pass it to the pure formatter functions.
 */
export function useDateFormat(): DateFormat {
  const qc = useQueryClient()
  const profile = qc.getQueryData<ProfileWithDateFormat>(['settings', 'profile'])
  return profile?.preferences?.date_format ?? DEFAULT_FORMAT
}
